import { Box, Stack, styled, Tooltip, Typography } from "@mui/material";
import { FC, MouseEventHandler } from "react";
import numeral from "numeral";
import { PrimaryClassification } from "@/gen/api/governance";
import { useDataCatalogContext } from "./dataCatalogContext/DataCatalogContext";


const BarItemRoot = styled(Box)({
  height: 48,
  position: 'relative',
  cursor: 'pointer',

  '& div.bar': {
    width: 12,
    borderRadius: 'unset',
  },
  '& div.bar-label': {
    position: 'absolute',
    width: 12,
    '& p': {
      textAlign: 'center',
    }
  },
  '& div.bar-number': {
    display: 'none',
    position: 'absolute',
    top: -20,
    width: 12,
    '& p': {
      textAlign: 'center',
    }
  },
  '&:hover div.bar-number': {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
})

const BarButtonRoot = styled(Box)({

})



interface BarProps {
  proportion: number,
  count: number,
  color: string,
  onClick: MouseEventHandler,
  label: string,
  title?: string,
}

const BarItem: FC<BarProps> = ({
  proportion,
  count,
  color,
  onClick,
  label,
  title,
}) => {
  return (
    <Tooltip title={title || label}>
      <BarItemRoot onClick={onClick}>
        <Box className="bar-number">
          <Typography variant="body2" color="info.main">{numeral(count).format('0,0')}</Typography>
        </Box>
        <Box className="space-holder" sx={{ height: `${100 - proportion}%` }}></Box>
        <BarButtonRoot className="bar" sx={{ bgcolor: color, height: `${proportion}%` }} />
        <Box className="bar-label">
          <Typography variant="body2" color="info.main">{label}</Typography>
        </Box>
      </BarItemRoot>
    </Tooltip>
  )
}


const ClassificationStatistics: FC = () => {
  const { pushCriteria, classificationStatistics } = useDataCatalogContext()

  const pickClassification = (c?: PrimaryClassification) => () => {
    if (c !== undefined) {
      pushCriteria({
        kind: 'Classification',
        primaryId: c,
        primaryName: PrimaryClassification[c],
        secondaryName: '',
      })
    } else {
      pushCriteria({
        kind: 'Classification',
        primaryId: null as any,
        primaryName: 'Pending Review',
        secondaryName: '',
      })
    }
  }
  
  const counts = [
    classificationStatistics?.publicCount || 0,
    classificationStatistics?.sensitiveCount || 0,
    classificationStatistics?.confidentialCount || 0,
    classificationStatistics?.regulatedCount || 0,
    classificationStatistics?.unclassifiedCount || 0,
  ]
  const max = Math.max(...counts, 1)
  const propotions = counts.map(i => Math.max((i * 100 / max)^0, 1))

  return (
    <Stack direction="row" alignItems="baseline" sx={{ mb: '32px !important' }}>
      <Typography variant="body1" color="text.secondary">Classifications:</Typography>
      <Stack direction="row" sx={{ pl: 1 }} spacing={1}>
        <BarItem 
          label="P"
          title="Public"
          color="#90DD75"
          proportion={propotions[0]}
          count={counts[0]}
          onClick={pickClassification(PrimaryClassification.Public)}
        />
        <BarItem 
          label="S"
          title="Sensitive"
          color="#DBDBDB"
          proportion={propotions[1]}
          count={counts[1]}
          onClick={pickClassification(PrimaryClassification.Sensitive)}
        />
        <BarItem 
          label="C"
          title="Confidential"
          color="#432D14"
          proportion={propotions[2]}
          count={counts[2]}
          onClick={pickClassification(PrimaryClassification.Confidential)}
        />
        <BarItem 
          label="R"
          title="Regulated"
          color="#0013C3"
          proportion={propotions[3]}
          count={counts[3]}
          onClick={pickClassification(PrimaryClassification.Regulated)}
        />
        <BarItem 
          label="-"
          title="Pending Review"
          color="#E50E00"
          proportion={propotions[4]}
          count={counts[4]}
          onClick={pickClassification()}
        />
      </Stack>
    </Stack>
  )
}

export default ClassificationStatistics;
